import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { ToolPageShell } from "@/components/AdminTools";
import {
  ShieldOff,
  ShieldCheck,
  TrendingDown,
  TrendingUp,
  Plus,
  Save,
  X,
  Pencil,
  Trash2,
  CheckCircle2,
  Clock,
  Lightbulb,
  Pause,
  RotateCcw,
  ChevronDown,
  ChevronRight,
  ExternalLink,
  Sparkles,
} from "lucide-react";

const PILLAR_ICONS = {
  phi_minimization: ShieldOff,
  hipaa_scope: ShieldCheck,
  cost_optimization: TrendingDown,
  revenue_optimization: TrendingUp,
};

const PILLAR_TINTS = {
  phi_minimization: { ring: "ring-violet-200", chip: "bg-violet-50 text-violet-800", bar: "bg-violet-500", soft: "bg-violet-50" },
  hipaa_scope: { ring: "ring-sky-200", chip: "bg-sky-50 text-sky-800", bar: "bg-sky-500", soft: "bg-sky-50" },
  cost_optimization: { ring: "ring-emerald-200", chip: "bg-emerald-50 text-emerald-800", bar: "bg-emerald-500", soft: "bg-emerald-50" },
  revenue_optimization: { ring: "ring-amber-200", chip: "bg-amber-50 text-amber-800", bar: "bg-amber-500", soft: "bg-amber-50" },
};

const STATUS_META = {
  idea:        { label: "Idea",        icon: Lightbulb,     cls: "bg-slate-100 text-slate-700" },
  in_progress: { label: "In progress", icon: Clock,          cls: "bg-amber-100 text-amber-800" },
  adopted:     { label: "Adopted",     icon: CheckCircle2,  cls: "bg-emerald-100 text-emerald-800" },
  parked:      { label: "Parked",      icon: Pause,         cls: "bg-slate-200 text-slate-600" },
};

const usd = (n) =>
  new Intl.NumberFormat("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }).format(n || 0);

export default function AdminPlatformStrategies() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [openIds, setOpenIds] = useState({});
  const [activePillar, setActivePillar] = useState("all");
  const [creating, setCreating] = useState(false);
  const [editing, setEditing] = useState(null); // { id, ...fields }
  const [form, setForm] = useState({
    pillar: "cost_optimization",
    title: "",
    summary: "",
    execution_steps: [""],
    impact_level: "medium",
    effort_level: "medium",
    estimated_savings_usd: 0,
  });

  const refresh = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/platform-strategies");
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    refresh();
  }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    if (activePillar === "all") return data.strategies;
    return data.strategies.filter((s) => s.pillar === activePillar);
  }, [data, activePillar]);

  const totalAdoptedSavings = useMemo(() => {
    if (!data) return 0;
    return Object.values(data.stats).reduce((acc, s) => acc + (s.estimated_savings_usd || 0), 0);
  }, [data]);

  const updateStatus = async (sid, status) => {
    try {
      await api.patch(`/admin/platform-strategies/${sid}`, { status });
      toast.success(`Marked ${STATUS_META[status]?.label || status}.`);
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const updateNotes = async (sid, notes) => {
    try {
      await api.patch(`/admin/platform-strategies/${sid}`, { notes });
      toast.success("Notes saved.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const deleteStrategy = async (sid) => {
    if (!window.confirm("Delete this strategy? Default seeded plays can be restored via Reset.")) return;
    try {
      await api.delete(`/admin/platform-strategies/${sid}`);
      toast.success("Deleted.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const resetDefaults = async () => {
    if (!window.confirm("Re-seed default strategies? Custom strategies you've added will be kept.")) return;
    try {
      await api.post("/admin/platform-strategies/reset");
      toast.success("Default plays restored.");
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const submitForm = async () => {
    const payload = {
      ...form,
      execution_steps: form.execution_steps.map((s) => s.trim()).filter(Boolean),
      estimated_savings_usd: parseFloat(form.estimated_savings_usd) || 0,
    };
    if (!payload.title?.trim()) {
      toast.error("Title is required.");
      return;
    }
    try {
      if (editing) {
        await api.patch(`/admin/platform-strategies/${editing.id}`, payload);
        toast.success("Strategy updated.");
      } else {
        await api.post("/admin/platform-strategies", payload);
        toast.success("Strategy added.");
      }
      setCreating(false);
      setEditing(null);
      setForm({
        pillar: "cost_optimization",
        title: "",
        summary: "",
        execution_steps: [""],
        impact_level: "medium",
        effort_level: "medium",
        estimated_savings_usd: 0,
      });
      refresh();
    } catch (e) {
      toast.error(formatApiError(e));
    }
  };

  const beginEdit = (s) => {
    setEditing(s);
    setCreating(true);
    setForm({
      pillar: s.pillar,
      title: s.title,
      summary: s.summary || "",
      execution_steps: s.execution_steps?.length ? [...s.execution_steps] : [""],
      impact_level: s.impact_level || "medium",
      effort_level: s.effort_level || "medium",
      estimated_savings_usd: s.estimated_savings_usd || 0,
    });
  };

  return (
    <ToolPageShell
      eyebrow="Admin · Strategy"
      title="Platform Strategies"
      intro="Curated business-optimization plays organized into four pillars: minimize PHI handling, reduce HIPAA scope, optimize Phase-1 fixed costs, and accelerate Phase-1 revenue. Mark each as adopted, parked, or in-progress; add your own. Defaults reseed without touching custom entries."
    >
      {loading && <div className="text-sm text-slate-500">Loading strategies…</div>}

      {!loading && data && (
        <div className="space-y-5" data-testid="platform-strategies">
          {/* KPI strip */}
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
            {Object.entries(data.pillars).map(([key, p]) => {
              const Icon = PILLAR_ICONS[key] || Lightbulb;
              const tint = PILLAR_TINTS[key] || PILLAR_TINTS.cost_optimization;
              const s = data.stats?.[key] || {};
              return (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActivePillar((c) => (c === key ? "all" : key))}
                  data-testid={`pillar-card-${key}`}
                  className={`text-left rounded-2xl border bg-white p-4 transition-all ${
                    activePillar === key ? `border-slate-900 ring-2 ${tint.ring}` : "border-slate-200 hover:border-slate-300"
                  }`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className={`inline-flex items-center justify-center w-7 h-7 rounded-lg ${tint.soft}`}>
                      <Icon className="w-4 h-4 text-slate-900" />
                    </span>
                    <div className="font-bold text-sm text-slate-900 leading-tight">{p.label}</div>
                  </div>
                  <div className="text-[11px] text-slate-500 leading-snug min-h-[34px]">{p.blurb}</div>
                  <div className="mt-3 flex items-center gap-2 flex-wrap">
                    <span className="text-[11px] font-bold text-slate-900 tabular-nums">{s.total || 0} plays</span>
                    {s.adopted ? (
                      <span className="text-[10px] inline-flex items-center gap-0.5 bg-emerald-50 text-emerald-800 rounded-full px-2 py-0.5 font-semibold">
                        <CheckCircle2 className="w-2.5 h-2.5" /> {s.adopted} adopted
                      </span>
                    ) : null}
                    {s.in_progress ? (
                      <span className="text-[10px] inline-flex items-center gap-0.5 bg-amber-50 text-amber-800 rounded-full px-2 py-0.5 font-semibold">
                        <Clock className="w-2.5 h-2.5" /> {s.in_progress} in progress
                      </span>
                    ) : null}
                  </div>
                  {s.estimated_savings_usd ? (
                    <div className="mt-2 text-[11px] text-emerald-700 font-bold tabular-nums">
                      {usd(s.estimated_savings_usd)}/mo committed
                    </div>
                  ) : null}
                </button>
              );
            })}
          </div>

          {/* Filter chip strip + toolbar */}
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <div className="flex items-center gap-2 flex-wrap">
              <button
                type="button"
                onClick={() => setActivePillar("all")}
                data-testid="pillar-filter-all"
                className={`text-xs font-semibold rounded-full px-3 py-1.5 ${activePillar === "all" ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
              >
                All ({data.strategies.length})
              </button>
              {Object.entries(data.pillars).map(([key, p]) => (
                <button
                  key={key}
                  type="button"
                  onClick={() => setActivePillar(key)}
                  data-testid={`pillar-filter-${key}`}
                  className={`text-xs font-semibold rounded-full px-3 py-1.5 ${activePillar === key ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"}`}
                >
                  {p.label}
                </button>
              ))}
            </div>
            <div className="flex items-center gap-2">
              {totalAdoptedSavings > 0 && (
                <span className="text-[11px] inline-flex items-center gap-1 bg-emerald-50 text-emerald-800 rounded-full px-2.5 py-1 font-bold">
                  <Sparkles className="w-3 h-3" /> {usd(totalAdoptedSavings)}/mo committed
                </span>
              )}
              <button
                type="button"
                onClick={() => {
                  setEditing(null);
                  setForm({
                    pillar: activePillar === "all" ? "cost_optimization" : activePillar,
                    title: "",
                    summary: "",
                    execution_steps: [""],
                    impact_level: "medium",
                    effort_level: "medium",
                    estimated_savings_usd: 0,
                  });
                  setCreating((c) => !c);
                }}
                data-testid="strategy-new-btn"
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold bg-slate-900 text-white hover:bg-slate-800"
              >
                <Plus className="w-3.5 h-3.5" /> New strategy
              </button>
              <button
                type="button"
                onClick={resetDefaults}
                data-testid="strategy-reset-btn"
                className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200"
              >
                <RotateCcw className="w-3.5 h-3.5" /> Reset defaults
              </button>
            </div>
          </div>

          {/* New / edit form */}
          {creating && (
            <div className="rounded-2xl border border-slate-200 bg-white p-5 space-y-3" data-testid="strategy-form">
              <div className="flex items-center justify-between">
                <div className="font-display font-bold text-slate-900">{editing ? "Edit strategy" : "Add strategy"}</div>
                <button
                  type="button"
                  onClick={() => { setCreating(false); setEditing(null); }}
                  className="text-slate-500 hover:text-slate-900"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              <div className="grid sm:grid-cols-2 gap-3">
                <label className="text-xs">
                  <span className="block font-semibold text-slate-700 mb-1">Pillar</span>
                  <select
                    value={form.pillar}
                    onChange={(e) => setForm({ ...form, pillar: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    data-testid="strategy-form-pillar"
                  >
                    {Object.entries(data.pillars).map(([k, p]) => (
                      <option key={k} value={k}>{p.label}</option>
                    ))}
                  </select>
                </label>
                <label className="text-xs">
                  <span className="block font-semibold text-slate-700 mb-1">Title</span>
                  <input
                    type="text"
                    value={form.title}
                    onChange={(e) => setForm({ ...form, title: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                    placeholder="e.g., Move marketing automation to Resend"
                    data-testid="strategy-form-title"
                  />
                </label>
              </div>
              <label className="text-xs block">
                <span className="block font-semibold text-slate-700 mb-1">Summary</span>
                <textarea
                  value={form.summary}
                  onChange={(e) => setForm({ ...form, summary: e.target.value })}
                  rows={2}
                  className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  placeholder="One-paragraph rationale + impact."
                  data-testid="strategy-form-summary"
                />
              </label>
              <div>
                <div className="text-xs font-semibold text-slate-700 mb-1">Execution steps</div>
                <div className="space-y-1.5">
                  {form.execution_steps.map((step, i) => (
                    <div key={`${step}-${i}`} className="flex items-start gap-2">
                      <span className="text-xs text-slate-400 font-bold pt-2">{i + 1}.</span>
                      <input
                        type="text"
                        value={step}
                        onChange={(e) => {
                          const next = [...form.execution_steps];
                          next[i] = e.target.value;
                          setForm({ ...form, execution_steps: next });
                        }}
                        className="flex-1 rounded-xl border border-slate-200 px-3 py-1.5 text-sm"
                        placeholder="Step description"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          const next = form.execution_steps.filter((_, j) => j !== i);
                          setForm({ ...form, execution_steps: next.length ? next : [""] });
                        }}
                        className="text-slate-400 hover:text-rose-700 pt-2"
                      >
                        <X className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  ))}
                </div>
                <button
                  type="button"
                  onClick={() => setForm({ ...form, execution_steps: [...form.execution_steps, ""] })}
                  className="mt-1.5 text-xs font-semibold text-slate-700 hover:text-slate-900 inline-flex items-center gap-1"
                >
                  <Plus className="w-3 h-3" /> Add step
                </button>
              </div>
              <div className="grid sm:grid-cols-3 gap-3">
                <label className="text-xs">
                  <span className="block font-semibold text-slate-700 mb-1">Impact</span>
                  <select
                    value={form.impact_level}
                    onChange={(e) => setForm({ ...form, impact_level: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </label>
                <label className="text-xs">
                  <span className="block font-semibold text-slate-700 mb-1">Effort</span>
                  <select
                    value={form.effort_level}
                    onChange={(e) => setForm({ ...form, effort_level: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm"
                  >
                    <option value="low">Low</option><option value="medium">Medium</option><option value="high">High</option>
                  </select>
                </label>
                <label className="text-xs">
                  <span className="block font-semibold text-slate-700 mb-1">Est. monthly savings (USD)</span>
                  <input
                    type="number"
                    value={form.estimated_savings_usd}
                    onChange={(e) => setForm({ ...form, estimated_savings_usd: e.target.value })}
                    className="w-full rounded-xl border border-slate-200 px-3 py-2 text-sm tabular-nums"
                  />
                </label>
              </div>
              <div className="flex items-center justify-end gap-2">
                <button
                  type="button"
                  onClick={() => { setCreating(false); setEditing(null); }}
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-semibold bg-slate-100 hover:bg-slate-200"
                >
                  Cancel
                </button>
                <button
                  type="button"
                  onClick={submitForm}
                  data-testid="strategy-form-save"
                  className="inline-flex items-center gap-1.5 rounded-full px-3.5 py-1.5 text-xs font-bold bg-slate-900 text-white hover:bg-slate-800"
                >
                  <Save className="w-3.5 h-3.5" /> {editing ? "Save changes" : "Add strategy"}
                </button>
              </div>
            </div>
          )}

          {/* Strategy cards */}
          <div className="grid lg:grid-cols-2 gap-3" data-testid="strategy-list">
            {filtered.map((s) => {
              const pillar = data.pillars[s.pillar];
              const tint = PILLAR_TINTS[s.pillar] || PILLAR_TINTS.cost_optimization;
              const Icon = PILLAR_ICONS[s.pillar] || Lightbulb;
              const isOpen = !!openIds[s.id];
              const status = STATUS_META[s.status] || STATUS_META.idea;
              const StatusIcon = status.icon;
              return (
                <div
                  key={s.id}
                  className="rounded-2xl border border-slate-200 bg-white overflow-hidden flex flex-col"
                  data-testid={`strategy-${s.id}`}
                >
                  <div className="p-4 flex items-start gap-3">
                    <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg ${tint.soft} flex-shrink-0`}>
                      <Icon className="w-4 h-4 text-slate-900" />
                    </span>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2 flex-wrap">
                        <div className="min-w-0">
                          <div className="text-[10px] font-bold uppercase tracking-wider text-slate-500">
                            {pillar?.label || s.pillar}
                          </div>
                          <h4 className="font-display font-bold text-slate-900 leading-snug">{s.title}</h4>
                        </div>
                        <span className={`inline-flex items-center gap-1 text-[10px] font-bold rounded-full px-2 py-0.5 ${status.cls}`}>
                          <StatusIcon className="w-2.5 h-2.5" /> {status.label}
                        </span>
                      </div>
                      {s.summary && <p className="text-[12px] text-slate-600 mt-1.5 leading-snug">{s.summary}</p>}
                      <div className="mt-2 flex flex-wrap items-center gap-1.5 text-[10px]">
                        <span className={`rounded-full px-1.5 py-0.5 font-bold ${tint.chip}`}>Impact · {s.impact_level}</span>
                        <span className="rounded-full px-1.5 py-0.5 font-bold bg-slate-100 text-slate-700">Effort · {s.effort_level}</span>
                        {s.estimated_savings_usd > 0 && (
                          <span className="rounded-full px-1.5 py-0.5 font-bold bg-emerald-50 text-emerald-800">
                            ~{usd(s.estimated_savings_usd)}/mo savings
                          </span>
                        )}
                      </div>
                    </div>
                  </div>

                  <div className="border-t border-slate-100 px-4 py-2 flex items-center justify-between gap-2 flex-wrap text-xs">
                    <div className="flex items-center gap-1">
                      {Object.entries(STATUS_META).map(([sk, m]) => (
                        <button
                          key={sk}
                          type="button"
                          onClick={() => updateStatus(s.id, sk)}
                          data-testid={`strategy-${s.id}-status-${sk}`}
                          title={`Mark ${m.label}`}
                          className={`text-[10px] font-bold rounded-full px-2 py-1 ${
                            s.status === sk ? "bg-slate-900 text-white" : "bg-slate-100 text-slate-700 hover:bg-slate-200"
                          }`}
                        >
                          {m.label}
                        </button>
                      ))}
                    </div>
                    <button
                      type="button"
                      onClick={() => setOpenIds((o) => ({ ...o, [s.id]: !o[s.id] }))}
                      data-testid={`strategy-${s.id}-toggle`}
                      className="inline-flex items-center gap-1 text-[11px] font-semibold text-slate-700 hover:text-slate-900"
                    >
                      {isOpen ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                      {isOpen ? "Hide" : "Show"} steps
                    </button>
                  </div>

                  {isOpen && (
                    <div className="border-t border-slate-100 px-4 py-3 bg-slate-50/60 space-y-3">
                      {s.execution_steps?.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1.5">Execution steps</div>
                          <ol className="text-[12px] text-slate-700 space-y-1 list-decimal list-inside">
                            {s.execution_steps.map((step, i) => (
                              <li key={`${step}-${i}`}>{step}</li>
                            ))}
                          </ol>
                        </div>
                      )}
                      {s.rationale_links?.length > 0 && (
                        <div>
                          <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">References</div>
                          <ul className="text-[11px] text-slate-600 space-y-0.5">
                            {s.rationale_links.map((l, i) => (
                              <li key={l || `link-${i}`} className="inline-flex items-center gap-1">
                                <ExternalLink className="w-2.5 h-2.5" /> {l}
                              </li>
                            ))}
                          </ul>
                        </div>
                      )}
                      <NotesEditor
                        sid={s.id}
                        initial={s.notes || ""}
                        onSave={(notes) => updateNotes(s.id, notes)}
                      />
                      <div className="flex items-center justify-end gap-2 pt-1">
                        <button
                          type="button"
                          onClick={() => beginEdit(s)}
                          data-testid={`strategy-${s.id}-edit`}
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-white border border-slate-200 hover:bg-slate-50"
                        >
                          <Pencil className="w-3 h-3" /> Edit
                        </button>
                        <button
                          type="button"
                          onClick={() => deleteStrategy(s.id)}
                          data-testid={`strategy-${s.id}-delete`}
                          className="inline-flex items-center gap-1 rounded-full px-2.5 py-1 text-[11px] font-semibold bg-white border border-rose-200 text-rose-700 hover:bg-rose-50"
                        >
                          <Trash2 className="w-3 h-3" /> Delete
                        </button>
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      )}
    </ToolPageShell>
  );
}

function NotesEditor({ sid, initial, onSave }) {
  const [val, setVal] = useState(initial);
  const [editing, setEditing] = useState(false);
  return (
    <div>
      <div className="text-[10px] uppercase tracking-wider font-bold text-slate-500 mb-1">Operator notes</div>
      {editing ? (
        <div className="flex items-start gap-2">
          <textarea
            value={val}
            onChange={(e) => setVal(e.target.value)}
            rows={2}
            className="flex-1 rounded-xl border border-slate-200 px-3 py-2 text-[12px]"
            data-testid={`strategy-${sid}-notes-input`}
          />
          <div className="flex flex-col gap-1">
            <button
              type="button"
              onClick={() => { onSave(val); setEditing(false); }}
              data-testid={`strategy-${sid}-notes-save`}
              className="rounded-full p-1 bg-slate-900 text-white hover:bg-slate-800"
            >
              <Save className="w-3 h-3" />
            </button>
            <button
              type="button"
              onClick={() => { setVal(initial); setEditing(false); }}
              className="rounded-full p-1 bg-slate-100 hover:bg-slate-200"
            >
              <X className="w-3 h-3" />
            </button>
          </div>
        </div>
      ) : (
        <button
          type="button"
          onClick={() => setEditing(true)}
          data-testid={`strategy-${sid}-notes-edit`}
          className="text-[12px] text-slate-600 italic w-full text-left rounded-lg border border-dashed border-slate-200 px-3 py-2 hover:border-slate-400"
        >
          {initial?.trim() ? initial : "Add operator notes…"}
        </button>
      )}
    </div>
  );
}
